import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HighchartsChartComponent, provideHighcharts } from 'highcharts-angular';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TrimValueAccessorDirective } from './shared/directives/trim-value-accessor/trim-value-accessor.directive';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { AgGridModule } from 'ag-grid-angular';
import { ClickOutsideModule } from 'ng-click-outside';
import { NgOtpInputModule } from 'ng-otp-input';
import { AutosizeModule } from 'ngx-autosize';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSelectModule } from 'ngx-select-ex';
import { ToastrModule } from 'ngx-toastr';
import { BlockUiComponent } from 'src/app/shared/components/block-ui/block-ui.component';
import { UiSwitchModule } from 'ngx-ui-switch';
import { environment } from '../environments/environment';
import { TourGuideTooltipModule } from './shared/components/tooltip/tooltip.module';
import { VideoQueueDashboardComponent } from './_admin/video-queue-dashboard/video-queue-dashboard.component';
import { PanoramerGlobalComponent } from './components/panoramer-global/panoramer-global.component';
import { authInterceptorProviders } from './_helpers/auth.interceptor';
import { ErrorCatchingInterceptor } from './_helpers/error-catching.interceptor';
import { ApplicationInsightsService } from './_services/application-insights.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AnnouncementWidgetComponent } from './components/announcement-widget/announcement-widget.component';
import { ConfirmmodalComponent } from './components/confirmmodal/confirmmodal.component';
import { EventsWidgetComponent } from './components/events-widget/events-widget.component';
import { FeedbackWidgetComponent } from './components/feedback-widget/feedback-widget.component';
import { NiederschlagWidgetComponent } from './components/niederschlag-widget/niederschlag-widget.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { WeatherWidgetComponent } from './components/weather-widget/weather-widget.component';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { CompliteRegistrationComponent } from './pages/complite-registration/complite-registration.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { ErrorPageComponent } from './pages/error-page/error-page.component';
import { HomeComponent } from './pages/home/home.component';
import { LoginOtpComponent } from './pages/login-otp/login-otp.component';
import { LoginComponent } from './pages/login/login.component';
import { OtpComponent } from './pages/otp/otp.component';
import { RecoveryPasswordComponent } from './pages/recovery-password/recovery-password.component';
import { SetPasswordComponent } from './pages/set-password/set-password.component';
import { SignUpComponent } from './pages/sign-up/sign-up.component';
import { BlobUrlPipe } from './shared/_pipes/blob-url.pipe';
import { RemoveDotPipe } from './shared/_pipes/remove-dot.pipe';
import { SafeHtmlPipe } from './shared/_pipes/safe-html.pipe';
import { ShortNamePipe } from './shared/_pipes/short-name.pipe';
import { TimePastPipe } from './shared/_pipes/time-past.pipe';
import { AgGridSharedModule } from './shared/ag-grid-components/ag-grid-shared.module';
import { DragAndDropModule } from './shared/components/drag-and-drop/drag-and-drop.module';
import { ResetColumnsModalModule } from './shared/components/reset-columns-modal/reset-columns-modal.module';
import { SharedNotificationModule } from './shared/shared-notification/shared-notification.module';
import { FileSizePipeStandalone } from './upload/_pipes/file-size.pipe';
import { IconByExtantionPipe } from './upload/_pipes/icon-by-extantion.pipe';
import { VisibleFlatStructure } from './upload/_pipes/visible-flat-structure.pipe';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

// MSAL Configuration Factory
export function MSALInstanceFactory(): IPublicClientApplication {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: environment.azureAd.clientId,
      authority: environment.azureAd.authority,
      redirectUri: environment.azureAd.redirectUri,
      postLogoutRedirectUri: environment.azureAd.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
  });

  // Initialize MSAL instance
  msalInstance.initialize().catch((error) => {
    console.error('MSAL initialization error:', error);
  });

  return msalInstance;
}

// MSAL Guard Configuration
export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: ['user.read', 'openid', 'profile', 'email'],
    },
  };
}

// MSAL Interceptor Configuration
export function MSALInterceptorConfigFactory() {
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap: new Map(),
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    DashboardComponent,
    ConfirmmodalComponent,
    LoginComponent,
    HomeComponent,
    SignUpComponent,
    RecoveryPasswordComponent,
    CompliteRegistrationComponent,
    ChangePasswordComponent,
    AnnouncementWidgetComponent,
    EventsWidgetComponent,
    WeatherWidgetComponent,
    FeedbackWidgetComponent,
    NiederschlagWidgetComponent,
    ErrorPageComponent,
    NotificationsComponent,
    LoginOtpComponent,
    OtpComponent,
    SetPasswordComponent,
    VideoQueueDashboardComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    BlockUiComponent,
    ModalModule,
    FormsModule,
    ReactiveFormsModule,
    TrimValueAccessorDirective,
    PopoverModule,
    NgxPaginationModule,
    BsDropdownModule,
    AutosizeModule,
    CarouselModule,
    ClickOutsideModule,
    NgxChartsModule,
    TooltipModule,
    FileSizePipeStandalone,
    NgxSelectModule,
    DragAndDropModule,
    SharedNotificationModule,
    UiSwitchModule,
    ScrollingModule,
    NgOtpInputModule,
    TimePastPipe,
    ShortNamePipe,
    IconByExtantionPipe,
    VisibleFlatStructure,
    RemoveDotPipe,
    SafeHtmlPipe,
    BlobUrlPipe,
    MsalModule,
    TourGuideTooltipModule,
    AgGridModule,
    AgGridSharedModule,
    ResetColumnsModalModule,
    PanoramerGlobalComponent,
    HighchartsChartComponent,
  ],
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorCatchingInterceptor,
      multi: true,
    },
    authInterceptorProviders,
    { provide: LOCALE_ID, useValue: 'de-DE' },
    ApplicationInsightsService,
    provideHighcharts({
      instance: () => import('highcharts').then((m) => m.default ?? m),
      modules: () => [import('highcharts/modules/stock')],
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
